import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scan, CheckCircle2, Package, FileText, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

export default function DamageSectionReceipt() {
  const navigate = useNavigate();
  const scanInputRef = useRef(null);
  const remarksInputRef = useRef(null);

  const [scanValue, setScanValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionLog, setSessionLog] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [showRemarks, setShowRemarks] = useState(false);
  const [freshRemarks, setFreshRemarks] = useState('');
  const [freshSourceSection, setFreshSourceSection] = useState('');
  const [freshUsn, setFreshUsn] = useState('');

  const focusScan = useCallback(() => {
    setTimeout(() => scanInputRef.current?.focus(), 80);
  }, []);

  const fetchPending = useCallback(async () => {
    try {
      const res = await apiClient.get('/damage/pending-receipt');
      setPendingCount(res.data.pending_count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchPending(); focusScan(); }, [fetchPending, focusScan]);

  const handleScan = async (e) => {
    e.preventDefault();
    const usn = scanValue.trim();
    if (!usn) return;

    setIsProcessing(true);
    try {
      const res = await apiClient.post('/damage/receive-usn', { usn_code: usn });

      const entry = {
        usn_code: usn,
        bundle_code: res.data.bundle_code,
        design_name: res.data.design_name,
        was_pre_marked: res.data.was_pre_marked,
        time: new Date().toLocaleTimeString()
      };
      setSessionLog(prev => [entry, ...prev]);
      toast.success(res.data.message);
      setScanValue('');
      fetchPending();
      focusScan();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      // If USN not found in system, show fresh receipt form
      if (err.response?.status === 404) {
        setFreshUsn(usn);
        setFreshRemarks('');
        setFreshSourceSection('');
        setShowRemarks(true);
        setTimeout(() => remarksInputRef.current?.focus(), 100);
      } else {
        toast.error(detail || 'Failed to receive USN');
        setScanValue('');
        focusScan();
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFreshReceipt = async () => {
    setIsProcessing(true);
    try {
      const res = await apiClient.post('/damage/receive-usn', {
        usn_code: freshUsn,
        remarks: freshRemarks.trim() || undefined,
        source_section: freshSourceSection.trim() || undefined
      });
      const entry = {
        usn_code: freshUsn,
        bundle_code: res.data.bundle_code,
        design_name: res.data.design_name,
        was_pre_marked: false,
        time: new Date().toLocaleTimeString()
      };
      setSessionLog(prev => [entry, ...prev]);
      toast.success(res.data.message);
      setShowRemarks(false);
      setScanValue('');
      fetchPending();
      focusScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to receive USN');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-base font-semibold">Damage Section — USN Receipt</h1>
            <p className="text-xs text-gray-400">Scan USNs to receive at Damage Section</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        {/* Pending count */}
        <Card className="border-amber-200 bg-amber-50" data-testid="pending-receipt-card">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">Pending Receipt</span>
            </div>
            <span className="text-lg font-bold text-amber-700">{pendingCount} USNs</span>
          </CardContent>
        </Card>

        {/* Scan input */}
        {!showRemarks && (
          <Card data-testid="scan-section">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Scan className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-sm">Scan USN to Receive</span>
              </div>
              <form onSubmit={handleScan}>
                <Input
                  ref={scanInputRef}
                  value={scanValue}
                  onChange={e => setScanValue(e.target.value)}
                  placeholder="Scan or enter USN barcode..."
                  disabled={isProcessing}
                  autoFocus
                  data-testid="usn-scan-input"
                />
              </form>
              {isProcessing && <p className="text-xs text-gray-500 mt-2">Processing...</p>}
            </CardContent>
          </Card>
        )}

        {/* Fresh receipt form (USN not pre-marked) */}
        {showRemarks && (
          <Card className="border-blue-300" data-testid="fresh-receipt-form">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-sm">New USN Receipt</span>
              </div>
              <p className="text-xs text-gray-500">USN <span className="font-mono font-medium">{freshUsn}</span> — not pre-marked as damaged. Provide details to receive directly.</p>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Source Section</label>
                <Input
                  value={freshSourceSection}
                  onChange={e => setFreshSourceSection(e.target.value)}
                  placeholder="e.g. WIP, FOB, Processing..."
                  data-testid="source-section-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Remarks (optional)</label>
                <Input
                  ref={remarksInputRef}
                  value={freshRemarks}
                  onChange={e => setFreshRemarks(e.target.value)}
                  placeholder="Describe the damage..."
                  data-testid="fresh-remarks-input"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleFreshReceipt} disabled={isProcessing} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-fresh-receipt-btn">
                  {isProcessing ? 'Processing...' : 'Receive at Damage Section'}
                </Button>
                <Button variant="outline" onClick={() => { setShowRemarks(false); setScanValue(''); focusScan(); }} data-testid="cancel-fresh-btn">
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Session log */}
        {sessionLog.length > 0 && (
          <Card data-testid="session-log">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Received Today ({sessionLog.length})</span>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {sessionLog.map((entry, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-xs">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="font-mono font-medium">{entry.usn_code}</span>
                      {entry.bundle_code && <span className="text-gray-400 ml-2">{entry.bundle_code}</span>}
                      {entry.design_name && <span className="text-gray-400 ml-1">&middot; {entry.design_name}</span>}
                    </div>
                    <span className="text-gray-400 shrink-0">{entry.time}</span>
                    {entry.was_pre_marked && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Pre-marked</span>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Link to damage inventory */}
        <Button
          variant="outline"
          className="w-full"
          onClick={() => navigate('/wms/damage/inventory')}
          data-testid="view-inventory-btn"
        >
          View Damage Inventory Report
        </Button>
      </div>
    </div>
  );
}
